<script setup lang="ts">
import { ref, inject, computed, onBeforeMount, reactive, Ref } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import cloneDeep from 'lodash/cloneDeep';
import { useRoute } from 'vue-router';
import routerKey from '@/router/routerKey';
import useVuelidate from '@vuelidate/core';
import { formatDate, resetTimer } from '@/utils/dateUtils';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { GQT_TAGENT, GQT_COMMERCIAL, GQT_REVENUE, QT_COORDINATOR, INCENTIVE_GROUP_TYPE, QT_AIR, QT_CRUISE, QT_INCENTIVE } from '@/constants';
import { handleBeforeSendImage, handleSelectFile as handleSelectFileNote } from '@/utils/helper';
import { helpers, required } from '@vuelidate/validators';
import { MAP_ROLE_TO_DEPARTMENT } from '@/constants/quoteTypes';
import { useAdminQuoteStore } from '@/store/AdminQuoteStore';
import { useMyQuoteStore } from '@/store/MyQuoteStore';
import { useUserInfo } from '@/modules/user/useUserInfo';
import { useViewComposable } from '@/views/composable';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Card from 'primevue/card';
import Checkbox from 'primevue/checkbox';
import FileUpload from 'primevue/fileupload';
import Image from 'primevue/image';
import InputText from 'primevue/inputtext';
import InputTextAera from 'primevue/textarea';
import MultiSelect from 'primevue/multiselect';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import Timeline from 'primevue/timeline';
import FilterNote from '@/components/notes/FilterNote.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const dialogRef = inject('dialogRef') as Ref;
const data = dialogRef?.value.data.data;
const qId = dialogRef?.value.data.quoteId;
const page = dialogRef?.value.data.page;
const serviceModule = dialogRef?.value.data.service;
const isRead = dialogRef?.value.data.isRead;

const { t } = useI18n({ useScope: 'global' });
const route = useRoute();
const toast = useToast();
const userInfo = useUserInfo();
const currentLanguage = getCurrentLocale();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const myQuoteStore = useMyQuoteStore();
const { departments } = useViewComposable();
const { renderLocalTimeFromUtc } = useViewComposable();
const { updateAcknowledgeNote, filterNote, preSigned } = useAdminQuoteStore();
const isAddNote = ref(false);
const activeType = ref(false);
const isShowCheckbox = ref(true);
const isShowError = ref(false);
const isShowErrorDocument = ref(false);
const isBlocked = ref(false);
const rules = ref<any>({});
const isLimit = ref(true);
const isImageUpload = ref(false);
const tabs = ref(
  page === 'MYQUOTE'
    ? [
        {
          title: t('note.external_note'),
          index: 1
        }
      ]
    : [
        {
          title: t('note.internal_note'),
          index: 0
        },
        {
          title: t('note.external_note'),
          index: 1
        }
      ]
);
const activeIndexTab = ref<number | string>(0);
const filterGlobal = ref('');
const isCheckBox = ref([]);
const userRolePublic = ref('');
const noteDataFilter = ref<any>(null);
const isLoading = ref<boolean>(false);

const noteList = computed(() => {
  let noteListTemp = serviceModule?.noteList;
  if (noteDataFilter.value) {
    noteListTemp = noteDataFilter.value;
  }
  if (filterGlobal.value) {
    noteListTemp = noteListTemp.filter((note: any) => note.content && note.content.toLowerCase().includes(filterGlobal.value.toLowerCase()));
    noteListTemp = noteListTemp.filter((note: any) => note.content && note.content.toLowerCase().includes(filterGlobal.value.toLowerCase()));
  }
  if (activeIndexTab.value || page === 'MYQUOTE') {
    return noteListTemp.filter((item: any) => item.createdBy === 'EXTERNAL');
  }
  return noteListTemp.filter((item: any) => item.createdBy === 'INTERNAL' || item.createdBy === 'SYSTEM');
});

const initEmptyNote = () => {
  return {
    title: '',
    content: '',
    isShowForAgency: isBlocked.value ? false : page === 'MYQUOTE',
    createdBy: activeIndexTab.value || page === 'MYQUOTE' ? 'EXTERNAL' : 'INTERNAL',
    isNoteRead: false,
    departments: []
  };
};

const newNote = reactive({
  ...initEmptyNote()
});

const newDocument = reactive({
  ...initEmptyNote()
});

const initRule = () => {
  rules.value = {};
  for (const property in newNote) {
    if (property !== 'departments' && activeIndexTab.value) {
      rules.value[property] = {
        required: helpers.withMessage(t('validate.required'), required)
      };
    }
    if (property !== 'departments' && !activeIndexTab.value && property !== 'title') {
      rules.value[property] = {
        required: helpers.withMessage(t('validate.required'), required)
      };
    }
  }
};

initRule();

let v$ = useVuelidate(rules, newNote);

const transformPayload = (payload: any) => {
  return {
    ...payload,
    departments: payload.departments.map((department: any) => ({
      department,
      isNoteRead: false
    }))
  };
};

const handleUpdateAcknowledgeNote = (payload: any) => {
  payload.forEach((item: any) => {
    const department = item.departments.find((dept: any) => dept.department === userRolePublic.value);
    if (department) {
      department.isNoteRead = true;
    }
  });

  updateAcknowledgeNote(qId, payload).then((res: any) => {
    if (res === 200 && payload.length) {
      toast.add({ severity: 'success', summary: '', detail: t('note.notification_acknowledged'), life: 3000 });
      isCheckBox.value = [];
    }
  });
};

const handleAssignDept = (data: any) => {
  if ((data.groupType && data.groupType !== INCENTIVE_GROUP_TYPE) || (data.gType && data.gType !== INCENTIVE_GROUP_TYPE)) {
    if (data.quoteID.includes('IM') || data.quoteID.includes('IQ') || data.quoteID.includes('HM')) {
      return QT_COORDINATOR;
    }
    if (data.quoteID.includes('AM')) {
      return QT_AIR;
    }
    if (data.quoteID.includes('CM')) {
      return QT_CRUISE;
    }
  }
  return QT_INCENTIVE;
};

const AddNote = () => {
  const departmentTemp = handleAssignDept(data);
  isShowError.value = true;
  if (!v$.value.$invalid) {
    let payload: any = { ...newNote };
    if (route.name !== routerKey.MY_QUOTE) {
      payload = transformPayload(payload);
      if (activeIndexTab.value) {
        payload.departments = [
          {
            department: GQT_TAGENT.toUpperCase(),
            isNoteRead: false
          }
        ];
      }
    }
    if (route.name === routerKey.MY_QUOTE) {
      payload.departments = [departmentTemp];
      payload = transformPayload(payload);
    }
    if (!payload.departments?.length && route.name !== routerKey.MY_QUOTE && !activeIndexTab.value) {
      return;
    }
    payload.externalRead = route.name === routerKey.MY_QUOTE;
    delete payload.isNoteRead;
    delete payload.isShowForAgency;
    isLoading.value = true;
    serviceModule
      .addNote(qId, payload)
      .then(() => {
        serviceModule.getNote(qId);
        isAddNote.value = false;
        Object.assign(newNote, initEmptyNote());
        toast.add({ severity: 'success', summary: '', detail: t('notification.submit_success'), life: 3000 });
      })
      .catch(() => {
        toast.add({ severity: 'error', summary: '', detail: t('notification.submit_error'), life: 5000 });
      })
      .finally(() => {
        isLoading.value = false;
      });
    isShowError.value = false;
  }
};

const handleSetActiveType = () => {
  activeType.value = !activeType.value;
};

const onCancelNote = () => {
  isAddNote.value = false;
  isShowError.value = false;
  Object.assign(newNote, initEmptyNote());
};

const isHasRoleNote = () => {
  if (route.name === routerKey.MY_QUOTE) {
    return authorizations?.my_quote?.note_add;
  }
  return authorizations?.quote_management?.note_add;
};

const fileUpload = ref();
const handleRemoveFile = () => {
  fileUpload.value.uploadedFileCount = 0;
  newDocument.imgUrl = '';
};

const onAdvancedUpload = (res) => {
  newDocument.imgUrl = res.xhr.response;
};

const uploadDocument = (document) => {
  const departmentTemp = handleAssignDept(data);
  isShowErrorDocument.value = true;
  if ((document.departments?.length && document.imgUrl) || (document.isShowForAgency && document.imgUrl) || (activeIndexTab.value && document.imgUrl)) {
    isLoading.value = true;
    let param = { ...document };
    if (route.name !== routerKey.MY_QUOTE) {
      param = transformPayload(param);
      if (activeIndexTab.value) {
        param.departments = [
          {
            department: GQT_TAGENT.toUpperCase(),
            isNoteRead: false
          }
        ];
      }
    }
    if (route.name === routerKey.MY_QUOTE) {
      param.departments = [departmentTemp];
      param = transformPayload(param);
    }
    param.isShowForAgency = document.isShowForAgency;
    param.createdBy = initEmptyNote().createdBy;
    param.externalRead = route.name === routerKey.MY_QUOTE;
    delete param.isNoteRead;
    delete param.isShowForAgency;
    serviceModule
      .addNote(qId, param)
      .then(async () => {
        await serviceModule.getNote(qId);
        fileUpload.value.remove();
        fileUpload.value.removeUploadedFile();
        Object.assign(newDocument, initEmptyNote());
        activeType.value = false;
      })
      .finally(() => {
        isLoading.value = false;
      });
    isShowErrorDocument.value = false;
  }
};

const onCancelDocument = () => {
  isShowErrorDocument.value = false;
  fileUpload.value.remove();
  fileUpload.value.removeUploadedFile();
  Object.assign(newDocument, initEmptyNote());
  activeType.value = false;
};

const handleDownloadImage = async (url: string) => {
  const urlTemp: string = await preSigned(url);
  await fetch(urlTemp)
    .then((res) => res.blob())
    .then((blob) => {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const date = new Date().toLocaleTimeString();
      link.download = `signature${date}`;
      link.click();
      URL.revokeObjectURL(link.href);
    });
};

const markNotesAsRead = () => {
  const noteTemps = cloneDeep(noteList.value);
  noteTemps.forEach((note: any) => {
    note.departments.forEach((department: any) => {
      if (department.department === userRolePublic.value) {
        department.isNoteRead = true;
      }
    });
    note.externalRead = true;
  });
  handleUpdateAcknowledgeNote(noteTemps);
};

onBeforeMount(async () => {
  const currentUser = await userInfo.getUserInfo();
  const userRole = currentUser?.role?.toUpperCase();
  if (MAP_ROLE_TO_DEPARTMENT[userRole]) {
    userRolePublic.value = MAP_ROLE_TO_DEPARTMENT[userRole];
  } else {
    userRolePublic.value = userRole;
  }
  if (route.name === routerKey.MY_QUOTE && isRead) {
    isShowCheckbox.value = false;
    markNotesAsRead();
  }
  if (userRole === GQT_TAGENT) {
    isShowCheckbox.value = false;
  }
  checkIsBlock(userRole);
});

const checkIsBlock = (role) => {
  if (role === GQT_COMMERCIAL || role === GQT_REVENUE) {
    isBlocked.value = true;
  }
};

const showFullNote = () => {
  isLimit.value = false;
};

const checkExtensionFile = (fileName) => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'jpe'];
  const fileExtension = fileName.split('.').pop().toLowerCase();
  return imageExtensions.includes(fileExtension);
};

const handleSelectFile = (event) => {
  if (checkExtensionFile(event.files[0].name)) {
    isImageUpload.value = true;
  } else {
    isImageUpload.value = false;
  }
  handleSelectFileNote();
};

const handleUrlBeforeUpload = (isImage) => {
  if (isImage) {
    return myQuoteStore.getUrlUploadImg;
  } else {
    return `${myQuoteStore.getUrlUploadFile}?isSecured=true`;
  }
};

const renderFileName = (url: string) => {
  const parts = url.split('/');
  return parts[parts.length - 1];
};

const renderHeader = (lang, quoteID) => {
  if (lang === 'fr') {
    return `${t('note.timeline')} de la ${t('note.quote')} ${quoteID}`;
  } else {
    return `${t('note.quote')}  ${quoteID} ${t('note.timeline')}`;
  }
};

const processText = (text: string) => {
  const textArray: any = [];
  const parts = text.split('**');

  parts.forEach((part: string, index: number) => {
    if (index % 2 === 1) {
      textArray.push({ content: part, isRed: true });
    } else {
      textArray.push({ content: part.replace(/^\s+/, ''), isRed: false });
    }
  });
  return textArray;
};

const updateActiveIndex = (index: string | number) => {
  isAddNote.value = false;
  isShowError.value = false;
  isShowErrorDocument.value = false;
  activeIndexTab.value = index;
  Object.assign(newNote, initEmptyNote());
  initRule();
  v$ = useVuelidate(rules, newNote);
  filterGlobal.value = '';
  noteDataFilter.value = null;
};

const isNoteUnreadForRole = (departments: any, isThumb = false) => {
  if (page !== 'MYQUOTE') {
    return departments.some((dept: any) => dept.department === userRolePublic.value && !dept.isNoteRead && (page !== 'MYQUOTE' || isThumb));
  }
  return isThumb && isRead;
};

const handleTitleNotification = (slotProps: any) => {
  const departmentTemp = handleAssignDept(data);
  if (slotProps.item.imgUrl && (activeIndexTab.value || page === 'MYQUOTE')) {
    return t('note.attachment');
  }
  return slotProps.item.title ? `${slotProps.item.title}` : `${t('note.attn')}: ${departmentTemp}`;
};

const handleSubmitFilter = (event: any) => {
  isLoading.value = true;
  const params: any = {
    fromDept: event?.departmentsCreated?.length ? event.departmentsCreated[0] : '',
    toDept: event?.departmentsTo?.length ? event.departmentsTo[0] : '',
    fromDate: resetTimer(event?.fromDate).replace('T00:00:00', '') || '',
    toDate: resetTimer(event?.toDate).replace('T00:00:00', '') || ''
  };
  if (!params.fromDept) {
    delete params.fromDept;
  }
  if (!params.toDept) {
    delete params.toDept;
  }
  if (!params.fromDate) {
    delete params.fromDate;
  }
  if (!params.toDate) {
    delete params.toDate;
  }
  const noteType: string = activeIndexTab.value || page === 'MYQUOTE' ? 'EXTERNAL' : 'INTERNAL';
  filterNote(qId, noteType, params)
    .then((res: any) => {
      noteDataFilter.value = res;
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.submit_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handleResetFilter = () => {
  noteDataFilter.value = null;
};

const isExternal = () => {
  return !!(activeIndexTab.value || page === 'MYQUOTE');
};

const isDepartmentsIncludes = (role: string) => {
  return role && role !== GQT_TAGENT.toUpperCase();
};
</script>

<template>
  <div class="file-notes">
    <LoadingSpinner v-if="isLoading" />
    <p>
      {{ $t('note.title') }} <b>{{ qId }}</b>
    </p>
    <div>
      <Tabs :value="0" @update:value="updateActiveIndex($event)">
        <TabList>
          <Tab v-for="tab in tabs" :key="tab.index" :value="tab.index">{{ tab.title }}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel v-for="tab in tabs" :key="tab.index" :value="tab.index"></TabPanel>
        </TabPanels>
      </Tabs>
    </div>
    <FilterNote :isExternal="isExternal()" @handleSubmitFilter="handleSubmitFilter($event)" @handleResetFilter="handleResetFilter()"></FilterNote>
    <div class="card mb-2">
      <div class="flex justify-content-between align-items-center">
        <h5>{{ renderHeader(currentLanguage, qId) }}</h5>
        <span class="p-input-icon-left mb-2">
          <i class="pi pi-search" />
          <InputText id="keyword-id" v-model="filterGlobal" :placeholder="$t('my_quote.search_keyword')" class="w-full" />
        </span>
      </div>
      <Timeline :value="isLimit && noteList?.length > 5 ? noteList.slice(0, 5) : noteList" align="alternate" class="customized-timeline">
        <template #marker="slotProps">
          <span class="custom-marker acvShadow" :style="{ backgroundColor: slotProps.item.color || '#e7201d' }" :class="{ 'opacity-50': !isNoteUnreadForRole(slotProps.item.departments, true) }">
            <i :class="slotProps.item.icon"></i>
          </span>
        </template>
        <template #content="slotProps">
          <div>
            <div class="mb-3" v-if="isNoteUnreadForRole(slotProps.item.departments)">
              <Checkbox :inputId="'acknowledged' + slotProps.item.noteNo" v-model="isCheckBox[slotProps.index]" :binary="true" @update:modelValue="handleUpdateAcknowledgeNote([slotProps.item])" />
              <label :for="'acknowledged' + slotProps.item.noteNo" class="ml-2"> {{ $t('note.acknowledge') }} </label>
            </div>
            <Card v-if="!slotProps.item.imgUrl" class="acvShadow note-item">
              <template #title>
                {{ handleTitleNotification(slotProps) }}
              </template>
              <template #subtitle>
                {{ slotProps.item.createdTime }}
              </template>
              <template #content>
                <div v-if="slotProps.item.content?.includes('**')">
                  <p v-for="(part, index) in processText(slotProps.item.content)" :key="index" class="mb-0">
                    <span v-if="part.isRed" class="highlight">{{ part.content }}</span>
                    <span v-else>{{ part.content }}</span>
                  </p>
                </div>
                <div v-else>
                  {{ slotProps.item.content }}
                </div>
              </template>
              <template #footer>
                <div>
                  <p class="text-right font-bold mt-3 mb-0">{{ slotProps.item?.signature }}</p>
                  <p v-if="isDepartmentsIncludes(slotProps.item.role)" class="text-right font-bold mb-0">{{ slotProps.item?.role }}</p>
                  <p class="text-right font-bold">{{ formatDate(renderLocalTimeFromUtc(slotProps.item.created), 'dd-LLL-yyyy HH:mm', true, currentLanguage) }}</p>
                </div>
              </template>
            </Card>
            <Card v-else class="acvShadow note-item">
              <template #title>
                {{ handleTitleNotification(slotProps) }}
              </template>
              <template #content>
                <div class="flex justify-content-center" v-if="checkExtensionFile(slotProps.item.imgUrl)">
                  <Image :src="slotProps.item.imgUrl" alt="Image" preview />
                </div>
                <div v-else>
                  <div class="flex justify-content-center">
                    <i class="pi pi-file text-3xl mb-1"></i>
                  </div>
                  <div class="font-bold mb-2 flex justify-content-center">{{ renderFileName(slotProps.item.imgUrl) }}</div>
                </div>
                <div class="upload-signature text-center pt-2">
                  <i class="pi pi-download cursor-pointer text-2xl" @click="handleDownloadImage(slotProps.item.imgUrl)"></i>
                </div>
              </template>
              <template #footer="">
                <div>
                  <p class="text-right font-bold mt-2 mb-0">{{ slotProps.item?.signature }}</p>
                  <p v-if="isDepartmentsIncludes(slotProps.item.role)" class="text-right font-bold mb-0">{{ slotProps.item?.role }}</p>
                  <p class="text-right font-bold">{{ formatDate(renderLocalTimeFromUtc(slotProps.item.created), 'dd-LLL-yyyy HH:mm', true, currentLanguage) }}</p>
                </div>
              </template>
            </Card>
          </div>
        </template>
      </Timeline>
      <div v-if="isLimit && noteList?.length > 5" class="flex justify-content-center">
        <Button class="show-more-button text-center cursor-pointer outline-none border-noround my-3" @click="showFullNote" :label="$t('common.buttons.show_more')" />
      </div>
    </div>
    <div v-if="isHasRoleNote()" class="flex justify-content-center">
      <Button class="add-note text-center cursor-pointer outline-none border-noround my-3" @click="isAddNote = !isAddNote" icon="pi pi-plus" :label="$t('note.add_note')" />
    </div>
    <div v-if="isAddNote" class="note-footer my-5">
      <div v-if="isShowCheckbox" class="flex align-items-center justify-content-between gap-2 mb-2">
        <div v-if="!activeIndexTab && page !== 'MYQUOTE'" class="flex gap-2">
          <div class="font-bold align-self-center width-label-dept">{{ $t('my_quote.directed_to_dept') }}:</div>
          <div class="flex flex-col w-full">
            <MultiSelect
              id="noteSelected"
              class="note-select"
              v-model="newNote.departments"
              :disabled="newNote.isShowForAgency"
              display="chip"
              :options="departments"
              optionLabel="label"
              option-value="value"
              :placeholder="$t('quote_management.department')"
              :maxSelectedLabels="5"
            />
            <span v-if="isShowError && !newNote.isShowForAgency && !newNote.departments?.length" class="text-red-500">
              {{ $t('validate.required') }}
            </span>
          </div>
        </div>
      </div>
      <div v-if="activeIndexTab" class="flex">
        <div class="w-full">
          <InputText v-model="newNote.title" maxlength="100" class="w-full note-title-input" :placeholder="$t('note.title_placeholder')" />
          <span v-if="isShowError && v$?.title?.$invalid" class="text-red-500">
            {{ v$.title?.required?.$message }}
          </span>
        </div>
      </div>
      <div class="flex flex-col flex-1 mt-2">
        <InputTextAera v-model="newNote.content" maxlength="1000" :placeholder="$t('note.content_placeholder')" class="w-full note-content-input" />
        <span v-if="isShowError && v$.content.$invalid" class="text-red-500">
          {{ v$.content?.required?.$message }}
        </span>
      </div>
      <div class="mt-3">
        <Button id="add-note-save" class="mr-2" :label="$t('common.buttons.save')" @click="AddNote()" />
        <Button class="add-note-cancel" :label="$t('common.buttons.cancel')" @click="onCancelNote()" />
      </div>
    </div>
    <div v-if="isHasRoleNote()" class="text-center cursor-pointer upload-signature note-footer-upload">
      <Button @click="handleSetActiveType()" class="upload-doc text-center cursor-pointer mb-3 outline-none border-noround" icon="pi pi-plus" :label="$t('note.upload_image')" />
      <div v-if="isShowCheckbox && activeType && !activeIndexTab && page !== 'MYQUOTE'" class="flex align-items-center justify-content-between gap-2 mt-2 mb-2">
        <div class="flex gap-2">
          <div class="font-bold align-self-center text-left width-label-dept">{{ $t('my_quote.directed_to_dept') }}:</div>
          <div class="flex flex-col w-full">
            <MultiSelect
              class="upload-select"
              v-model="newDocument.departments"
              :disabled="newDocument.isShowForAgency"
              display="chip"
              :options="departments"
              optionLabel="label"
              option-value="value"
              :placeholder="$t('quote_management.department')"
              :maxSelectedLabels="5"
            />
            <span v-if="isShowErrorDocument && !newDocument.isShowForAgency && !newDocument.departments?.length" class="text-red-500">
              {{ $t('validate.required') }}
            </span>
          </div>
        </div>
      </div>
      <FileUpload
        class="upload-section"
        v-if="activeType"
        name="file"
        @before-send="handleBeforeSendImage"
        @select="handleSelectFile($event)"
        :withCredentials="true"
        ref="fileUpload"
        :url="handleUrlBeforeUpload(isImageUpload)"
        @removeUploadedFile="handleRemoveFile()"
        @upload="onAdvancedUpload($event)"
        :chooseLabel="$t('promos.labels.choose')"
        :uploadLabel="$t('promos.labels.upload')"
        :cancelLabel="$t('promos.labels.cancel')"
        accept=".png,.gif,.jpeg,.jpg,.jpe,.xlsx,.xls,.pdf,.docx,.doc,.txt,.csv"
        :maxFileSize="5000000"
        :file-limit="1"
        :invalid-file-size-message="$t('notification.validSizeImg')"
      >
        <template #empty>
          <p>{{ $t('promos.labels.drag_file_desc') }}</p>
        </template>
        <template> </template>
      </FileUpload>
      <div class="mt-1 text-left">
        <span v-if="isShowErrorDocument && !newDocument.imgUrl" class="text-red-500"> {{ $t('validate.press_upload') }} </span>
      </div>
      <div v-if="activeType" class="mt-3 text-left">
        <Button class="mr-2 upload-save" :label="$t('common.buttons.save')" @click="uploadDocument(newDocument)" />
        <Button class="upload-cancel" :label="$t('common.buttons.cancel')" @click="onCancelDocument()" />
      </div>
    </div>
  </div>
</template>
